import { apiFetch, responseSupport } from "@/lib/api";

/**
 * Read-only client for the admin console API (`/admin/api/*`, ALL-5773).
 *
 * These routes are served by alloy-admin-console and reached on the same origin
 * with the browser session cookie. The edge verifies the session against the
 * gateway (`GET /v1/auth/session/permissions`) and admin-console repeats that
 * check itself (`requireAdminSession`): one admin gate scope, and the admin must
 * belong to the organization the console serves. Nothing here adds a scope or
 * decides access on the client — a refusal is rendered exactly as the server
 * gave it. Only GET is used; the console's one mutation (POST /api/probes) is
 * deliberately not wrapped.
 */

export type AdminConsoleDenial = "unauthenticated" | "admin_scope_required" | "admin_organization_mismatch" | "forbidden";

export interface AdminConsoleFailure {
  ok: false;
  status: number;
  /** Bounded reason code from the body (`reason`/`reason_code`), else `HTTP_<status>`. */
  reasonCode: string;
  /** Set for 401/403 so the page can render a permission state instead of an error. */
  denial?: AdminConsoleDenial;
  supportReference?: string;
  capability?: string;
}

export interface AdminConsoleSuccess<T> {
  ok: true;
  data: T;
}

export type AdminConsoleResult<T> = AdminConsoleSuccess<T> | AdminConsoleFailure;

const REASON = /^[A-Za-z0-9_.:-]{1,96}$/;

function boundedReason(value: unknown): string | undefined {
  return typeof value === "string" && REASON.test(value.trim()) ? value.trim() : undefined;
}

function denialFor(status: number, reason: string | undefined): AdminConsoleDenial | undefined {
  if (status === 401) return "unauthenticated";
  if (status !== 403) return undefined;
  if (reason === "admin_scope_required") return "admin_scope_required";
  if (reason === "admin_organization_mismatch") return "admin_organization_mismatch";
  return "forbidden";
}

export async function adminConsoleGet<T>(path: string, scope: string): Promise<AdminConsoleResult<T>> {
  let res: Response;
  try {
    res = await apiFetch(`/admin/api${path}`, {}, scope);
  } catch {
    return { ok: false, status: 0, reasonCode: "NETWORK_ERROR" };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const reason = boundedReason(body?.reason) ?? boundedReason(body?.reason_code) ?? boundedReason(body?.reasonCode);
    return {
      ok: false,
      status: res.status,
      reasonCode: reason ?? `HTTP_${res.status}`,
      denial: denialFor(res.status, reason),
      ...responseSupport(res, body ?? undefined),
    };
  }
  if (!body || typeof body !== "object") {
    return { ok: false, status: res.status, reasonCode: "MALFORMED_RESPONSE", ...responseSupport(res) };
  }
  return { ok: true, data: body as T };
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim() !== "") : [];
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

// --- D1: trace lookup (GET /admin/api/diagnostics/search) ---------------------

export interface TraceEvent {
  id: string;
  sourceService?: string;
  eventFamily?: string;
  eventType?: string;
  outcome?: string;
  reasonCode?: string;
  supportReference?: string;
  occurredAt?: string;
  remediationCode?: string;
  routeClass?: string;
  httpRoute?: string;
  httpMethod?: string;
  httpStatus?: string;
  failingFields: string[];
  /** `field:code` pairs, keyed by field name. */
  failingFieldCodes: Record<string, string>;
  clientRequestId?: string;
  traceId?: string;
  requestId?: string;
}

export interface TraceSearch {
  /** `ready` when the diagnostics source answered; `degraded` when it could not be read. */
  status: "ready" | "degraded" | "unknown";
  /** Reason the source could not be read (only when degraded). */
  reason?: string;
  /**
   * Set when the search ran and the service answered "no event for this reference in your
   * organization" through a refusal (see normalizeTraceSearch). Shown as a detail so a
   * misconfigured scope that refused every reference stays visible rather than silent.
   */
  notFoundCode?: string;
  events: TraceEvent[];
  returnedCount: number;
  dataBoundary?: string;
}

function fieldCodes(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const entry of strList(value)) {
    const at = entry.lastIndexOf(":");
    if (at > 0 && at < entry.length - 1) out[entry.slice(0, at)] = entry.slice(at + 1);
  }
  return out;
}

export function normalizeTraceSearch(body: Record<string, unknown>): TraceSearch {
  const rawEvents = Array.isArray(body.events) ? body.events : [];
  const events: TraceEvent[] = rawEvents
    .filter((event): event is Record<string, unknown> => !!event && typeof event === "object")
    .map((event, index) => ({
      id: str(event.diagnosticEventId) ?? str(event.sourceEventId) ?? `event-${index}`,
      sourceService: str(event.sourceService),
      eventFamily: str(event.eventFamily),
      eventType: str(event.eventType),
      outcome: str(event.outcome),
      reasonCode: str(event.reasonCode),
      supportReference: str(event.supportReference),
      occurredAt: str(event.occurredAt),
      remediationCode: str(event.remediationCode),
      routeClass: str(event.routeClass),
      httpRoute: str(event.httpRoute),
      httpMethod: str(event.httpMethod),
      httpStatus: str(event.httpStatus) ?? (num(event.httpStatus) !== undefined ? String(event.httpStatus) : undefined),
      failingFields: strList(event.failingFields),
      failingFieldCodes: fieldCodes(event.failingFieldCodes),
      clientRequestId: str(event.clientRequestId),
      traceId: str(event.traceId),
      requestId: str(event.requestId),
    }));
  const status = body.status === "ready" || body.status === "degraded" ? body.status : "unknown";
  const reason = status === "degraded" ? boundedReason(body.reason) ?? "diagnostics_search_unavailable" : undefined;
  // ALL-5773 (SUP-106): the Diagnostics Service answers a well-formed reference that has no event in
  // the caller's organization with 400 DIAGNOSTIC_SEARCH_SCOPE_MISMATCH (deliberately the same answer
  // for "never recorded" and "another organization's", SUP-104), and admin-console passes that on as
  // a degraded search with reason diagnostics_service_http_400. The page validates the reference
  // before searching, so that 400 means "no event for this reference here": it is reported as no
  // events, never as an unreachable source. A 5xx or timeout stays degraded.
  if (status === "degraded" && reason === "diagnostics_service_http_400") {
    return {
      status: "ready",
      notFoundCode: reason,
      events: [],
      returnedCount: 0,
      dataBoundary: str(body.dataBoundary),
    };
  }
  return {
    status,
    reason,
    events,
    returnedCount: num(body.returnedCount) ?? events.length,
    dataBoundary: str(body.dataBoundary),
  };
}

/** Support references are short bounded tokens (`sr-01a0f24a`); anything else is refused locally. */
export const SUPPORT_REFERENCE_INPUT = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

export async function searchTrace(supportReference: string): Promise<AdminConsoleResult<TraceSearch>> {
  const qs = new URLSearchParams({ supportReference, limit: "25" });
  const result = await adminConsoleGet<Record<string, unknown>>(`/diagnostics/search?${qs.toString()}`, "admin-trace-lookup");
  return result.ok ? { ok: true, data: normalizeTraceSearch(result.data) } : result;
}

const FAILURE_OUTCOMES = new Set(["failed", "failure", "denied", "deny", "blocked", "rejected", "error", "timeout"]);
const SUCCESS_OUTCOMES = new Set(["success", "succeeded", "allowed", "allow", "ok", "completed", "accepted"]);

/** A recorded failure must never read as green; unknown outcomes stay neutral (ALL-5427). */
export function outcomeTone(outcome: string | undefined): "danger" | "success" | "neutral" {
  const value = (outcome ?? "").trim().toLowerCase();
  if (FAILURE_OUTCOMES.has(value)) return "danger";
  if (SUCCESS_OUTCOMES.has(value)) return "success";
  return "neutral";
}

// --- M1/C5: service explorer (GET /admin/api/service-explorer) -----------------

export type ServiceStatus = "up" | "degraded" | "down" | "unknown";

export interface ServiceProbe {
  endpoint: string;
  status: ServiceStatus;
  latencyMs?: number;
  httpStatus?: number;
  /** Bounded failure token (`probe_timeout`, `tcp_timeout`, ...); never a URL. */
  error?: string;
}

export interface ServiceConfigEntry {
  key: string;
  value: string;
  source?: string;
}

export interface ExplorerService {
  id: string;
  name: string;
  module?: string;
  purpose?: string;
  owner?: string;
  status: ServiceStatus;
  capabilities: string[];
  dependencies: string[];
  endpointCount: number;
  probes: ServiceProbe[];
  config: ServiceConfigEntry[];
}

export interface ServiceExplorer {
  /** When the probe snapshot was taken (`deployment.generated_at`). */
  checkedAt?: string;
  summary: { serviceCount: number; up: number; degraded: number; down: number };
  services: ExplorerService[];
}

function serviceStatus(value: unknown): ServiceStatus {
  return value === "up" || value === "degraded" || value === "down" ? value : "unknown";
}

const PROBE_ERROR = /^[a-z][a-z0-9_]{0,63}$/;

function displayValue(value: unknown): string | undefined {
  if (typeof value === "string") return value.trim() ? value : undefined;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

export function normalizeServiceExplorer(body: Record<string, unknown>): ServiceExplorer {
  const rawServices = Array.isArray(body.services) ? body.services : [];
  const services: ExplorerService[] = rawServices
    .filter((service): service is Record<string, unknown> => !!service && typeof service === "object")
    .map((service, index) => {
      const probes = (Array.isArray(service.probes) ? service.probes : [])
        .filter((probe): probe is Record<string, unknown> => !!probe && typeof probe === "object")
        .map((probe) => {
          const error = str(probe.error);
          return {
            endpoint: str(probe.endpoint) ?? "Endpoint",
            status: serviceStatus(probe.status),
            latencyMs: num(probe.latency_ms),
            httpStatus: num(probe.http_status),
            // Socket errors can carry host:port text; only bounded tokens are shown.
            error: error && PROBE_ERROR.test(error) ? error : error ? "probe_failed" : undefined,
          };
        });
      const config = (Array.isArray(service.config) ? service.config : [])
        .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object")
        .flatMap((entry): ServiceConfigEntry[] => {
          const key = str(entry.key);
          const value = displayValue(entry.value);
          return key && value !== undefined ? [{ key, value, source: str(entry.source) }] : [];
        });
      return {
        id: str(service.id) ?? str(service.service_id) ?? `service-${index}`,
        name: str(service.name) ?? str(service.id) ?? "Unnamed service",
        module: str(service.module),
        purpose: str(service.purpose),
        owner: str(service.owner),
        status: serviceStatus(service.status),
        capabilities: strList(service.capabilities),
        dependencies: strList(service.dependencies),
        endpointCount: num(service.endpoint_count) ?? (Array.isArray(service.endpoints) ? service.endpoints.length : 0),
        probes,
        config,
      };
    });
  const summary = body.summary && typeof body.summary === "object" ? (body.summary as Record<string, unknown>) : {};
  const count = (status: ServiceStatus) => services.filter((service) => service.status === status).length;
  const deployment = body.deployment && typeof body.deployment === "object" ? (body.deployment as Record<string, unknown>) : {};
  return {
    checkedAt: str(deployment.generated_at),
    summary: {
      serviceCount: num(summary.service_count) ?? services.length,
      up: num(summary.up) ?? count("up"),
      degraded: num(summary.degraded) ?? count("degraded"),
      down: num(summary.down) ?? count("down"),
    },
    // service_explorer.warnings are catalog-provenance notes for Alloy operators
    // (internal status page), not customer copy, so they are not carried here.
    services,
  };
}

export async function fetchServiceExplorer(): Promise<AdminConsoleResult<ServiceExplorer>> {
  const result = await adminConsoleGet<Record<string, unknown>>("/service-explorer", "admin-service-explorer");
  return result.ok ? { ok: true, data: normalizeServiceExplorer(result.data) } : result;
}

// --- D3/D4: sign-in and wallet-page access diagnostics -------------------------

export interface SignInEvent {
  id: string;
  occurredAt?: string;
  eventType?: string;
  outcome?: string;
  reasonCode?: string;
  supportReference?: string;
  remediationHint?: string;
  principalHandle?: string;
  routeClass?: string;
  serviceName?: string;
}

export interface SignInDiagnostics {
  status: "ready" | "degraded" | "unknown";
  reason?: string;
  events: SignInEvent[];
  dataBoundary?: string;
}

export function normalizeSignInDiagnostics(body: Record<string, unknown>): SignInDiagnostics {
  const raw = Array.isArray(body.events) ? body.events : [];
  const events = raw
    .filter((event): event is Record<string, unknown> => !!event && typeof event === "object")
    .map((event, index) => ({
      id: str(event.eventId) ?? `event-${index}`,
      occurredAt: str(event.occurredAt),
      eventType: str(event.eventType),
      outcome: str(event.outcome),
      reasonCode: str(event.reasonCode),
      supportReference: str(event.supportReference),
      remediationHint: str(event.remediationHint),
      principalHandle: str(event.principalHandle),
      routeClass: str(event.routeClass),
      serviceName: str(event.serviceName),
    }));
  const status = body.status === "ready" || body.status === "degraded" ? body.status : "unknown";
  return {
    status,
    reason: status === "degraded" ? boundedReason(body.reason) ?? "auth_diagnostics_unavailable" : undefined,
    events,
    dataBoundary: str(body.dataBoundary),
  };
}

export async function searchSignInDiagnostics(filters: { reference?: string; reasonCode?: string }): Promise<AdminConsoleResult<SignInDiagnostics>> {
  const qs = new URLSearchParams({ limit: "25" });
  if (filters.reference) qs.set("correlationId", filters.reference);
  if (filters.reasonCode) qs.set("reasonCode", filters.reasonCode);
  const result = await adminConsoleGet<Record<string, unknown>>(`/auth-diagnostics?${qs.toString()}`, "admin-signin-diagnostics");
  return result.ok ? { ok: true, data: normalizeSignInDiagnostics(result.data) } : result;
}

export interface AdminTool {
  id: string;
  action?: string;
  safeToRunBy?: string;
  mutatesAccess: boolean;
}

export interface WalletAccessCheck {
  status: string;
  reasonCode?: string;
  likelyFailureDomain?: string;
  nextAction?: string;
  generatedAt?: string;
  expectedScope?: string;
  expectedPermission?: string;
  /** Read-only wallet list probe the console runs with its own credential. */
  probeStatus?: string;
  sourceStatus?: string;
  tools: AdminTool[];
}

export function normalizeWalletAccessCheck(body: Record<string, unknown>): WalletAccessCheck | null {
  const d = body.access_diagnostics && typeof body.access_diagnostics === "object" ? (body.access_diagnostics as Record<string, unknown>) : null;
  if (!d) return null;
  const contract = d.expected_access_contract && typeof d.expected_access_contract === "object" ? (d.expected_access_contract as Record<string, unknown>) : {};
  const posture = d.source_posture && typeof d.source_posture === "object" ? (d.source_posture as Record<string, unknown>) : {};
  const probe = posture.gateway_wallet_read_probe && typeof posture.gateway_wallet_read_probe === "object" ? (posture.gateway_wallet_read_probe as Record<string, unknown>) : {};
  const source = posture.auth_diagnostics && typeof posture.auth_diagnostics === "object" ? (posture.auth_diagnostics as Record<string, unknown>) : {};
  const tools = (Array.isArray(d.admin_tools) ? d.admin_tools : [])
    .filter((tool): tool is Record<string, unknown> => !!tool && typeof tool === "object" && typeof tool.id === "string")
    .map((tool) => ({
      id: tool.id as string,
      action: str(tool.action),
      safeToRunBy: str(tool.safe_to_run_by),
      // Anything not explicitly false is treated as changing access.
      mutatesAccess: tool.mutates_access !== false,
    }));
  return {
    status: str(d.status) ?? "unknown",
    reasonCode: boundedReason(d.reason_code),
    likelyFailureDomain: boundedReason(d.likely_failure_domain),
    nextAction: boundedReason(d.next_action),
    generatedAt: str(d.generated_at),
    expectedScope: str(contract.expected_scope),
    expectedPermission: str(contract.expected_permission),
    probeStatus: boundedReason(probe.status),
    sourceStatus: boundedReason(source.status),
    tools,
  };
}

export async function fetchWalletAccessCheck(): Promise<AdminConsoleResult<WalletAccessCheck | null>> {
  const result = await adminConsoleGet<Record<string, unknown>>("/access-diagnostics/wallet-page", "admin-wallet-access-check");
  return result.ok ? { ok: true, data: normalizeWalletAccessCheck(result.data) } : result;
}

// --- M2/P1/I2: deployment posture (GET /admin/api/deployment) ------------------

export type LifecycleStatus = "ready" | "in_progress" | "blocked" | "unknown";

export interface LifecycleCheck {
  id: string;
  label: string;
  status: string;
  evidence?: string;
}

export interface LifecycleStage {
  id: string;
  title: string;
  purpose?: string;
  status: LifecycleStatus;
  checks: LifecycleCheck[];
}

export interface HelpSection {
  title: string;
  checks: string[];
  escalation?: string;
}

export interface ExternalHandoff {
  id: string;
  target: string;
  useWhen?: string;
  /** Only an https URL the deployment configured; anything else is dropped. */
  url?: string;
}

export interface DeploymentPosture {
  environment?: string;
  releaseVersion?: string;
  commitSha?: string;
  region?: string;
  cluster?: string;
  generatedAt?: string;
  summary: { serviceCount?: number; up?: number; degraded?: number; down?: number };
  lifecycle: LifecycleStage[];
  helpSections: HelpSection[];
  handoffs: ExternalHandoff[];
}

function lifecycleStatus(value: unknown): LifecycleStatus {
  return value === "ready" || value === "in_progress" || value === "blocked" ? value : "unknown";
}

function objects(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => !!item && typeof item === "object") : [];
}

function httpsUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function normalizeDeploymentPosture(body: Record<string, unknown>): DeploymentPosture {
  const deployment = body.deployment && typeof body.deployment === "object" ? (body.deployment as Record<string, unknown>) : {};
  const summary = body.summary && typeof body.summary === "object" ? (body.summary as Record<string, unknown>) : {};
  return {
    environment: str(deployment.environment),
    releaseVersion: str(deployment.release_version),
    commitSha: str(deployment.commit_sha),
    region: str(deployment.region),
    cluster: str(deployment.cluster),
    generatedAt: str(deployment.generated_at),
    summary: { serviceCount: num(summary.service_count), up: num(summary.up), degraded: num(summary.degraded), down: num(summary.down) },
    lifecycle: objects(body.deployment_lifecycle).map((stage, index) => ({
      id: str(stage.id) ?? `stage-${index}`,
      title: str(stage.title) ?? str(stage.id) ?? "Stage",
      purpose: str(stage.purpose),
      status: lifecycleStatus(stage.status),
      checks: objects(stage.checks).map((check, checkIndex) => ({
        id: str(check.id) ?? `check-${checkIndex}`,
        label: str(check.label) ?? str(check.id) ?? "Check",
        status: str(check.status) ?? "unknown",
        evidence: str(check.evidence),
      })),
    })),
    helpSections: objects(body.help_sections).map((section) => ({
      title: str(section.title) ?? "Help",
      checks: strList(section.checks),
      escalation: str(section.escalation),
    })),
    handoffs: objects(body.external_handoffs).map((handoff, index) => {
      const url = handoff.url && typeof handoff.url === "object" ? (handoff.url as Record<string, unknown>) : {};
      return {
        id: str(handoff.id) ?? `handoff-${index}`,
        target: str(handoff.target) ?? "External system",
        useWhen: str(handoff.use_when),
        url: url.configured === true ? httpsUrl(url.url) : undefined,
      };
    }),
  };
}

export async function fetchDeploymentPosture(): Promise<AdminConsoleResult<DeploymentPosture>> {
  const result = await adminConsoleGet<Record<string, unknown>>("/deployment", "admin-deployment-posture");
  return result.ok ? { ok: true, data: normalizeDeploymentPosture(result.data) } : result;
}
